import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { CheckCircle2, MessagesSquare, Smile, Timer } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { fetchAnalytics } from '../api';
import { RATING_FACES, RatingFace, RATINGS } from '../ratings';
import { Analytics as AnalyticsData } from '../types';

// Brand blue and an orange far enough from it in every color-vision mode
// (checked with the dataviz validator), so "started" and "closed" never blur.
const STARTED_COLOR = '#3D5D98';
const CLOSED_COLOR = '#E8833A';
const GRID_COLOR = '#E9ECF3';
const AXIS_COLOR = '#8A94A6';

const RANGES = [7, 30, 90] as const;

export const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

const shortDay = (iso: string): string =>
  // Noon so no timezone offset can push the label onto the neighbouring day.
  new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

const Card: React.FC<{ title?: string; className?: string; children: React.ReactNode }> = ({
  title,
  className,
  children,
}) => (
  <section
    className={classNames(
      'p-5 border rounded-xl bg-white-0.0 border-white-2.0 shadow-sm',
      className
    )}
  >
    {title && <h3 className="mb-4 text-sm font-semibold text-black-1.0">{title}</h3>}
    {children}
  </section>
);

type TileProps = {
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon: React.ReactNode;
  tint: string;
};

const Tile: React.FC<TileProps> = ({ label, value, hint, icon, tint }: TileProps) => (
  <div className="flex items-start gap-4 p-5 border rounded-xl bg-white-0.0 border-white-2.0 shadow-sm">
    <span
      className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full"
      style={{ backgroundColor: `${tint}1A`, color: tint }}
    >
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-xs font-medium tracking-wide uppercase text-gray-1.0">{label}</p>
      <div className="mt-1 text-2xl font-semibold leading-tight text-black-1.0">{value}</div>
      {hint && <p className="mt-1 text-xs text-gray-1.0">{hint}</p>}
    </div>
  </div>
);

// Recharts' default tooltip is a grey box with a black border; this one
// matches the cards around it.
const ChartTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: { name: string; value: number; color: string }[];
}> = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0 || !label) return null;
  return (
    <div className="px-3 py-2 text-xs border rounded-lg shadow-md bg-white-0.0 border-white-2.0">
      <p className="mb-1 font-semibold text-black-1.0">{shortDay(label)}</p>
      {payload.map((row) => (
        <p key={row.name} className="flex items-center gap-2 text-gray-3.0">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: row.color }} />
          {row.name}
          <span className="ml-auto font-semibold text-black-1.0">{row.value}</span>
        </p>
      ))}
    </div>
  );
};

type Props = {
  onOpenConversation: (id: string) => void;
};

/**
 * How the queue is being resolved and how customers feel about it: volume,
 * first response, resolution time and the five-face ratings from the widget.
 */
const Analytics: React.FC<Props> = ({ onOpenConversation }: Props) => {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setFailed(false);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    fetchAnalytics(days, tz)
      .then(({ data: payload }) => {
        if (active) setData(payload);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [days]);

  const ratingTotal = data ? data.ratings.count : 0;

  return (
    <div className="flex-1 p-6 overflow-y-auto bg-white-1.0">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-black-1.0">Support analytics</h2>
          <p className="text-sm text-gray-1.0">How conversations are resolved and rated</p>
        </div>
        <div
          className="inline-flex p-1 border rounded-lg bg-white-0.0 border-white-2.0 shadow-sm"
          role="group"
          aria-label="Date range"
        >
          {RANGES.map((range) => (
            <button
              key={range}
              type="button"
              aria-pressed={days === range}
              onClick={() => setDays(range)}
              className={classNames(
                'px-3 py-1 text-sm font-medium rounded-md transition-colors',
                days === range
                  ? 'bg-primary-2.0 text-white-0.0 shadow-sm'
                  : 'text-gray-3.0 hover:bg-white-1.0'
              )}
            >
              {range}d
            </button>
          ))}
        </div>
      </div>

      {failed && <p className="text-gray-1.0">Could not load the analytics.</p>}
      {!failed && !data && <p className="text-gray-1.0">Loading…</p>}

      {!failed && data && (
        <div className="space-y-5">
          <div className="sm:grid-cols-2 xl:grid-cols-4 grid grid-cols-1 gap-4">
            <Tile
              label="Conversations"
              value={data.totals.started}
              hint={`${data.totals.open_now} open right now`}
              icon={<MessagesSquare size={20} />}
              tint={STARTED_COLOR}
            />
            <Tile
              label="Resolved"
              value={data.totals.closed}
              hint={`Median ${formatDuration(data.resolution.median_seconds)} to close`}
              icon={<CheckCircle2 size={20} />}
              tint="#1F9D6A"
            />
            <Tile
              label="First response"
              value={formatDuration(data.first_response.median_seconds)}
              hint={`Median · ${data.totals.unanswered} still unanswered`}
              icon={<Timer size={20} />}
              tint={CLOSED_COLOR}
            />
            <Tile
              label="Satisfaction"
              value={
                data.ratings.average === null ? (
                  '—'
                ) : (
                  <span className="flex items-center gap-2">
                    <span>
                      {data.ratings.average.toFixed(1)}
                      <span className="text-base font-normal text-gray-1.0"> / 5</span>
                    </span>
                    <RatingFace rating={Math.round(data.ratings.average)} size={28} />
                  </span>
                )
              }
              hint={
                data.ratings.response_rate === null
                  ? `${data.ratings.count} ratings`
                  : `${data.ratings.count} ratings · ${Math.round(
                      data.ratings.response_rate * 100
                    )}% of resolved`
              }
              icon={<Smile size={20} />}
              tint="#8B5CF6"
            />
          </div>

          <div className="lg:grid-cols-3 grid gap-5">
            <Card title="Started and resolved per day" className="lg:col-span-2">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={data.daily}
                  margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                  barGap={2}
                >
                  <CartesianGrid vertical={false} stroke={GRID_COLOR} />
                  <XAxis
                    dataKey="day"
                    tickFormatter={shortDay}
                    tick={{ fontSize: 11, fill: AXIS_COLOR }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={16}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: AXIS_COLOR }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F2F4F8' }} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  />
                  <Bar
                    dataKey="started"
                    name="Started"
                    fill={STARTED_COLOR}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={18}
                  />
                  <Bar
                    dataKey="closed"
                    name="Resolved"
                    fill={CLOSED_COLOR}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Ratings">
              <ul className="space-y-3">
                {[...RATINGS].reverse().map((score) => {
                  const count = data.ratings.distribution[String(score)] ?? 0;
                  const share = ratingTotal ? count / ratingTotal : 0;
                  return (
                    <li key={score} className="flex items-center gap-3 text-sm">
                      <RatingFace rating={score} size={26} />
                      <span className="flex-1">
                        <span className="flex justify-between mb-1 text-xs">
                          <span className="text-gray-3.0">{RATING_FACES[score].label}</span>
                          <span className="text-gray-1.0">
                            {count} · {Math.round(share * 100)}%
                          </span>
                        </span>
                        <span className="block h-2 overflow-hidden rounded-full bg-white-1.0">
                          <span
                            className="block h-2 transition-all duration-500 rounded-full bg-primary-2.0"
                            style={{ width: `${share * 100}%` }}
                          />
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>

          <div className="lg:grid-cols-2 grid gap-5">
            <Card title="By agent">
              {data.agents.length === 0 ? (
                <p className="text-sm text-gray-1.0">Nothing in this range.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-left uppercase border-b text-gray-1.0 border-white-2.0">
                      <th className="pb-2 font-medium">Agent</th>
                      <th className="pb-2 font-medium text-right">Resolved</th>
                      <th className="pb-2 font-medium text-right">1st response</th>
                      <th className="pb-2 font-medium text-right">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white-1.0">
                    {data.agents.map((agent) => (
                      <tr key={agent.agent_id ?? 'unassigned'}>
                        <td className="py-2.5 text-black-1.0">{agent.name}</td>
                        <td className="py-2.5 text-right tabular-nums">{agent.closed}</td>
                        <td className="py-2.5 text-right tabular-nums">
                          {formatDuration(agent.first_response.median_seconds)}
                        </td>
                        <td className="py-2.5 text-right">
                          {agent.ratings.average === null ? (
                            '—'
                          ) : (
                            <span className="inline-flex items-center justify-end gap-1.5 tabular-nums">
                              <RatingFace
                                rating={Math.round(agent.ratings.average)}
                                size={18}
                                labelled={false}
                              />
                              {agent.ratings.average.toFixed(1)}
                              <span className="text-xs text-gray-1.0">({agent.ratings.count})</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <Card title="Latest ratings">
              {data.recent_ratings.length === 0 ? (
                <p className="text-sm text-gray-1.0">No ratings in this range yet.</p>
              ) : (
                <ul className="-mx-2">
                  {data.recent_ratings.map((row) => (
                    <li key={row.conversation_id}>
                      <button
                        type="button"
                        onClick={() => onOpenConversation(row.conversation_id)}
                        className="flex items-center w-full gap-3 px-2 py-2 text-sm text-left transition-colors rounded-lg hover:bg-white-1.0"
                      >
                        <RatingFace rating={row.rating} size={26} />
                        <span className="flex-1 min-w-0">
                          <span className="block truncate text-black-1.0">
                            {row.contact_name || 'Unknown'}
                          </span>
                          <span className="block text-xs truncate text-gray-1.0">
                            {row.agent_name ? `Answered by ${row.agent_name}` : 'Unassigned'}
                          </span>
                        </span>
                        <span className="text-xs text-gray-1.0">
                          {new Date(row.rated_at).toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
